import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/api/axios'
import { useToast } from 'vue-toastification'
import dayjs from 'dayjs'

export const useScheduleStore = defineStore('schedule', () => {
  const toast = useToast()
  
  // State
  const scheduledCalls = ref([])
  const availableSlots = ref([])
  const loading = ref(false)
  const error = ref(null)
  const selectedDate = ref(new Date())
  
  // Getters
  const todaysSchedule = computed(() => {
    const today = dayjs().format('YYYY-MM-DD')
    return scheduledCalls.value.filter(call => 
      dayjs(call.scheduled_time).format('YYYY-MM-DD') === today
    ).sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time))
  })
  
  const weekSchedule = computed(() => {
    const startOfWeek = dayjs().startOf('week')
    const endOfWeek = dayjs().endOf('week')
    
    return scheduledCalls.value.filter(call => {
      const callDate = dayjs(call.scheduled_time)
      return callDate.isAfter(startOfWeek) && callDate.isBefore(endOfWeek)
    })
  })
  
  const monthSchedule = computed(() => {
    const month = dayjs(selectedDate.value).format('YYYY-MM')
    return scheduledCalls.value.filter(call => 
      dayjs(call.scheduled_time).format('YYYY-MM') === month
    )
  })
  
  const upcomingCalls = computed(() => {
    const now = new Date()
    return scheduledCalls.value
      .filter(call => new Date(call.scheduled_time) > now)
      .sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time))
      .slice(0, 5)
  })
  
  // Actions
  async function fetchSchedule(params = {}) {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.get('/calls/schedule/', { params })
      scheduledCalls.value = response.data.scheduled_calls
      return response.data
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to fetch schedule'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  async function scheduleCall(data) {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post('/calls/schedule/', data)
      scheduledCalls.value.push(response.data.scheduled_call)
      toast.success('Call scheduled successfully!')
      return response.data.scheduled_call
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to schedule call'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  async function updateScheduledCall(id, data) {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.put(`/calls/schedule/${id}`, data)
      
      const index = scheduledCalls.value.findIndex(c => c.id === parseInt(id))
      if (index !== -1) {
        scheduledCalls.value[index] = response.data.scheduled_call
      }
      
      toast.success('Schedule updated successfully!')
      return response.data.scheduled_call
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to update schedule'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  async function cancelScheduledCall(id, reason = '') {
    loading.value = true
    error.value = null
    
    try {
      await api.delete(`/calls/schedule/${id}`, {
        data: { reason }
      })
      
      scheduledCalls.value = scheduledCalls.value.filter(c => c.id !== parseInt(id))
      toast.success('Scheduled call cancelled')
      
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to cancel scheduled call'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  async function fetchAvailableSlots(profileId, date) {
    try {
      const response = await api.get('/calls/available-slots', {
        params: { profile_id: profileId, date }
      })
      availableSlots.value = response.data.slots
      return response.data.slots
    } catch (err) {
      toast.error('Failed to fetch available slots')
      throw err
    }
  }
  
  async function bulkSchedule(profileIds, schedule) {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post('/calls/bulk-schedule', {
        profile_ids: profileIds,
        schedule
      })
      
      scheduledCalls.value.push(...response.data.scheduled_calls)
      toast.success(`${response.data.scheduled_calls.length} calls scheduled successfully!`)
      return response.data
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to bulk schedule calls'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  async function createRecurringSchedule(profileId, pattern) {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post('/calls/recurring-schedule', {
        profile_id: profileId,
        pattern
      })
      
      scheduledCalls.value.push(...response.data.scheduled_calls)
      toast.success('Recurring schedule created successfully!')
      return response.data
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to create recurring schedule'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  function setSelectedDate(date) {
    selectedDate.value = date
  }
  
  return {
    // State
    scheduledCalls,
    availableSlots,
    loading,
    error,
    selectedDate,
    
    // Getters
    todaysSchedule,
    weekSchedule,
    monthSchedule,
    upcomingCalls,
    
    // Actions
    fetchSchedule,
    scheduleCall,
    updateScheduledCall,
    cancelScheduledCall,
    fetchAvailableSlots,
    bulkSchedule,
    createRecurringSchedule,
    setSelectedDate
  }
})
